import apiClient from '../config/api';
import type { Hospital, HospitalListResponse } from '../types';

export interface ListHospitalsParams {
  search?: string;
  active?: string;
  page?: number;
  limit?: number;
}

// Map snake_case API response to camelCase Hospital type
function mapHospital(data: Record<string, unknown>): Hospital {
  return {
    hospitalUuid: data.hospital_uuid as string,
    hospitalId: data.hospital_id as number,
    hospitalName: data.hospital_name as string,
    hospitalCode: data.hospital_code as string,
    isActive: data.is_active as boolean,
    createdBy: data.created_by as string,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
  };
}

export async function listHospitals(params: ListHospitalsParams = {}): Promise<HospitalListResponse> {
  const query: Record<string, string> = {};
  if (params.search) query.search = params.search;
  if (params.active) query.active = params.active;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const response = await apiClient.get('/api/hospitals', { params: query });
  const data = response.data;

  return {
    hospitals: (data.hospitals || []).map(mapHospital),
    total: data.total,
    page: data.page,
    limit: data.limit,
  };
}

export async function getHospital(hospitalUuid: string): Promise<Hospital> {
  const response = await apiClient.get(`/api/hospitals/${hospitalUuid}`);
  return mapHospital(response.data.hospital);
}

export async function createHospital(hospitalName: string, hospitalCode: string): Promise<Hospital> {
  const response = await apiClient.post('/api/hospitals', { hospitalName, hospitalCode });
  return mapHospital(response.data.hospital);
}

export async function updateHospital(
  hospitalUuid: string,
  updates: { hospitalName?: string; hospitalCode?: string; isActive?: boolean }
): Promise<Hospital> {
  const response = await apiClient.put(`/api/hospitals/${hospitalUuid}`, updates);
  return mapHospital(response.data.hospital);
}

export async function deleteHospital(hospitalUuid: string): Promise<void> {
  await apiClient.delete(`/api/hospitals/${hospitalUuid}`);
}

export interface HospitalHistoryEntry {
  historyId: number;
  hospitalUuid: string;
  hospitalId: number;
  hospitalName: string;
  hospitalCode: string;
  isActive: boolean;
  changeType: string;
  changedBy: string;
  changedAt: string;
  previousValues: Record<string, unknown> | null;
}

export async function getHospitalHistory(hospitalUuid: string): Promise<HospitalHistoryEntry[]> {
  const response = await apiClient.get(`/api/hospitals/${hospitalUuid}/history`);
  return (response.data.history || []).map((entry: Record<string, unknown>) => ({
    historyId: entry.history_id,
    hospitalUuid: entry.hospital_uuid,
    hospitalId: entry.hospital_id,
    hospitalName: entry.hospital_name,
    hospitalCode: entry.hospital_code,
    isActive: entry.is_active,
    changeType: entry.change_type,
    changedBy: entry.changed_by,
    changedAt: entry.changed_at,
    previousValues: entry.previous_values,
  }));
}
